import styled from "@emotion/styled";
import { theme } from "../../lib/theme";
import type { TxFilterChip } from "../../lib/tx-filters";

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  flex-wrap: wrap;
  margin-bottom: ${theme.spacing.md};
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: 2px ${theme.spacing.sm};
  border: 1px solid ${theme.colors.primary}66;
  border-radius: ${theme.radius.md};
  background: ${theme.colors.primary}18;
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.text};
`;

const ChipLabel = styled.span`
  color: ${theme.colors.textMuted};
`;

const ChipValue = styled.span`
  font-family: monospace;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${theme.colors.textMuted};
  font-size: ${theme.fontSize.sm};
  line-height: 1;

  &:hover {
    color: ${theme.colors.text};
  }
`;

const ClearAll = styled.button`
  background: none;
  border: none;
  padding: 2px ${theme.spacing.xs};
  cursor: pointer;
  color: ${theme.colors.textMuted};
  font-size: ${theme.fontSize.xs};
  text-decoration: underline;

  &:hover {
    color: ${theme.colors.text};
  }
`;

export interface ActiveFilterChipsProps {
  chips: TxFilterChip[];
  /** Omit both handlers for a read-only row (the saved-view page). */
  onRemove?: (key: TxFilterChip["key"]) => void;
  onClearAll?: () => void;
}

export function ActiveFilterChips({ chips, onRemove, onClearAll }: ActiveFilterChipsProps) {
  if (chips.length === 0) return null;
  return (
    <Row>
      {chips.map((chip) => (
        <Chip key={chip.key}>
          <ChipLabel>{chip.label}</ChipLabel>
          <ChipValue>{chip.text}</ChipValue>
          {onRemove && (
            <RemoveButton onClick={() => onRemove(chip.key)} title={`Remove ${chip.label} filter`}>
              ×
            </RemoveButton>
          )}
        </Chip>
      ))}
      {chips.length > 1 && onClearAll && <ClearAll onClick={onClearAll}>Clear all</ClearAll>}
    </Row>
  );
}
